import { defaultTagId } from './chunk-AHISJLEK.js';
import { familyFor } from './chunk-DI5BDT3Z.js';
import { pricedAboveDust, claimedProtocols, FALLBACK_PROTOCOL_ID, BARE_TRANSFER_PROTOCOLS } from './chunk-RAO7SHWG.js';
import { requireForensicParam, forensicCheck, clampForensicParam } from './chunk-SAW4RQG7.js';

// src/poisoning/index.ts
var POISONING_CHECK_ID = "evm.address_poisoning";
var POLICY_PARAMS = Object.freeze({
  minSharedPrefix: "min_shared_prefix",
  minSharedSuffix: "min_shared_suffix",
  maxMirrorShare: "max_mirror_share",
  mirrorWindowSeconds: "mirror_window_seconds"
});
var POLICY_FIELDS = Object.keys(POLICY_PARAMS);
var OPTIONAL_POLICY_FIELDS = /* @__PURE__ */ new Set([
  "maxMirrorShare",
  "mirrorWindowSeconds"
]);
var POISONING_POLICY_PARAMS = Object.freeze({
  minSharedPrefix: requireForensicParam(POISONING_CHECK_ID, POLICY_PARAMS.minSharedPrefix),
  minSharedSuffix: requireForensicParam(POISONING_CHECK_ID, POLICY_PARAMS.minSharedSuffix),
  maxMirrorShare: requireForensicParam(POISONING_CHECK_ID, POLICY_PARAMS.maxMirrorShare),
  mirrorWindowSeconds: requireForensicParam(POISONING_CHECK_ID, POLICY_PARAMS.mirrorWindowSeconds)
});
var DEFAULT_POISONING_POLICY = Object.freeze({
  minSharedPrefix: POISONING_POLICY_PARAMS.minSharedPrefix.default,
  minSharedSuffix: POISONING_POLICY_PARAMS.minSharedSuffix.default,
  maxMirrorShare: POISONING_POLICY_PARAMS.maxMirrorShare.default,
  mirrorWindowSeconds: POISONING_POLICY_PARAMS.mirrorWindowSeconds.default
});
function poisoningPolicyFromParams(params) {
  const read = (field) => {
    const value = params?.[POLICY_PARAMS[field]];
    return typeof value === "number" && Number.isFinite(value) ? value : DEFAULT_POISONING_POLICY[field];
  };
  return {
    minSharedPrefix: read("minSharedPrefix"),
    minSharedSuffix: read("minSharedSuffix"),
    maxMirrorShare: read("maxMirrorShare"),
    mirrorWindowSeconds: read("mirrorWindowSeconds")
  };
}
function clampPoisoningPolicy(policy) {
  const out = {
    minSharedPrefix: clampForensicParam(POISONING_POLICY_PARAMS.minSharedPrefix, policy.minSharedPrefix),
    minSharedSuffix: clampForensicParam(POISONING_POLICY_PARAMS.minSharedSuffix, policy.minSharedSuffix)
  };
  if (policy.maxMirrorShare !== void 0) {
    out.maxMirrorShare = clampForensicParam(POISONING_POLICY_PARAMS.maxMirrorShare, policy.maxMirrorShare);
  }
  if (policy.mirrorWindowSeconds !== void 0) {
    out.mirrorWindowSeconds = clampForensicParam(POISONING_POLICY_PARAMS.mirrorWindowSeconds, policy.mirrorWindowSeconds);
  }
  return out;
}
function poisoningPolicyWithinLimits(policy) {
  const clamped = clampPoisoningPolicy(policy);
  return POLICY_FIELDS.every((field) => {
    const value = policy[field];
    if (value === void 0) return OPTIONAL_POLICY_FIELDS.has(field);
    return Number.isFinite(value) && clamped[field] === value;
  });
}
function mirrorLimits(policy) {
  return {
    share: policy.maxMirrorShare ?? DEFAULT_POISONING_POLICY.maxMirrorShare,
    window: policy.mirrorWindowSeconds ?? DEFAULT_POISONING_POLICY.mirrorWindowSeconds
  };
}
var POISONING_CATALOG_TAGS = Object.freeze(
  (forensicCheck(POISONING_CHECK_ID)?.tags ?? []).map((tag) => tag.name)
);
var POISONING_TAG_KEYS = new Set(
  POISONING_CATALOG_TAGS.flatMap((name) => [foldTag(name), defaultTagId(name)])
);
function foldTag(value) {
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_");
}
function isPoisoningCatalogTag(value) {
  const text = (value ?? "").trim();
  if (!text) return false;
  return POISONING_TAG_KEYS.has(text) || POISONING_TAG_KEYS.has(foldTag(text));
}
var NATIVE_ASSET = "native";
var EVM_ADDRESS = /^0x[0-9a-f]{40}$/;
function evmAddress(address) {
  const a = (address ?? "").toLowerCase();
  return EVM_ADDRESS.test(a) ? a : null;
}
var normalise = evmAddress;
var B58 = "1-9A-HJ-NP-Za-km-z";
var TRON_ADDRESS = new RegExp(`^T[${B58}]{33}$`);
var BASE58_ADDRESS = new RegExp(`^[${B58}]{25,44}$`);
var XRPL_ADDRESS = new RegExp(`^r[${B58}]{24,34}$`);
var BECH32_ADDRESS = /^([a-z][a-z0-9]{0,82})1([02-9ac-hj-np-z]{6,})$/;
var SEGWIT_HRPS = /* @__PURE__ */ new Set(["bc", "tb", "bcrt", "ltc", "tltc", "rltc"]);
var LEGACY_BASE58_MAX = 35;
function bech32Form(text, family) {
  const lowered = text.toLowerCase();
  if (text !== lowered && text !== text.toUpperCase()) return null;
  const match = BECH32_ADDRESS.exec(lowered);
  if (!match) return null;
  const hrp = match[1];
  const data = match[2];
  const segwit = SEGWIT_HRPS.has(hrp);
  if (family !== null && family !== "utxo" && family !== "cosmos" || family === "cosmos" && segwit) return null;
  const lead = segwit ? `${hrp}1${data[0]}` : `${hrp}1`;
  return { family: segwit ? "utxo" : "cosmos", lead, body: lowered.slice(lead.length) };
}
function displayForm(address, chain) {
  const text = (address ?? "").trim();
  if (!text) return null;
  const family = chain ? familyFor(chain) : null;
  const lowered = text.toLowerCase();
  if (EVM_ADDRESS.test(lowered)) {
    if (family !== null && family !== "evm" && family !== "hypercore") return null;
    return { family: "evm", lead: "0x", body: lowered.slice(2) };
  }
  if (family === "evm" || family === "hypercore") return null;
  if ((family === null || family === "tron") && TRON_ADDRESS.test(text)) {
    return { family: "tron", lead: "T", body: text.slice(1) };
  }
  if (family === "tron") return null;
  if (family === "xrpl") return XRPL_ADDRESS.test(text) ? { family: "xrpl", lead: "r", body: text.slice(1) } : null;
  const bech32 = bech32Form(text, family);
  if (bech32) return bech32;
  if ((family === null || family === "solana" || family === "utxo") && BASE58_ADDRESS.test(text)) {
    const legacy = family === "utxo" || family === null && text.length <= LEGACY_BASE58_MAX;
    if (legacy) return { family: "utxo", lead: text[0], body: text.slice(1) };
    return { family: "solana", lead: "", body: text };
  }
  return null;
}
function poisoningAddress(address, chain) {
  const form = displayForm(address, chain);
  return form ? form.lead + form.body : null;
}
function formAffixes(x, y) {
  if (x.family !== y.family || x.lead !== y.lead) return null;
  const a = x.body;
  const b = y.body;
  const limit = Math.min(a.length, b.length);
  let prefix = 0;
  while (prefix < limit && a[prefix] === b[prefix]) prefix += 1;
  let suffix = 0;
  while (suffix < limit && a[a.length - 1 - suffix] === b[b.length - 1 - suffix]) suffix += 1;
  return { prefix, suffix };
}
function lookalikeAffixes(x, y, policy) {
  if (!x || !y || x.body === y.body) return null;
  const shared = formAffixes(x, y);
  return shared && shared.prefix >= policy.minSharedPrefix && shared.suffix >= policy.minSharedSuffix ? shared : null;
}
function sharedAffixes(a, b, chain) {
  const x = displayForm(a, chain);
  const y = displayForm(b, chain);
  return x && y ? formAffixes(x, y) : null;
}
function isLookalike(a, b, policy = DEFAULT_POISONING_POLICY, chain) {
  return lookalikeAffixes(displayForm(a, chain), displayForm(b, chain), policy) !== null;
}
function rawAmount(value) {
  return typeof value === "bigint" ? value : amount(value);
}
function assetKey(token) {
  const text = (token ?? "").trim();
  if (!text) return null;
  if (text.toLowerCase() === NATIVE_ASSET) return NATIVE_ASSET;
  return poisoningAddress(text) ?? text;
}
function finite(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
function mirrorKey(candidate) {
  return [
    candidate.canonicalTxId ?? "",
    poisoningAddress(candidate.fromAddress) ?? "",
    poisoningAddress(candidate.toAddress) ?? "",
    assetKey(candidate.token) ?? "",
    rawAmount(candidate.amountRaw)?.toString() ?? ""
  ].join("|");
}
function movedRealValue(row) {
  return pricedAboveDust(row.valueUsd) || row.provenValue === true;
}
function provenPayment(row) {
  const moved = rawAmount(row.amountRaw);
  if (assetKey(row.token) === NATIVE_ASSET && moved !== null && moved > 0n) return true;
  return movedRealValue(row);
}
function valueShare(small, large) {
  const asset = assetKey(small.token);
  if (asset !== null && asset === assetKey(large.token)) {
    const a2 = rawAmount(small.amountRaw);
    const b2 = rawAmount(large.amountRaw);
    if (a2 !== null && b2 !== null) return b2 > 0n ? Number(a2) / Number(b2) : null;
  }
  const a = finite(small.valueUsd);
  const b = finite(large.valueUsd);
  return a !== null && b !== null && b > 0 ? a / b : null;
}
function mirrorOf(sender, candidate, payments, paidAt, formOf, policy) {
  const { share: maxShare, window } = mirrorLimits(policy);
  if (!(maxShare > 0)) return null;
  const at = finite(candidate.timestamp);
  if (at === null) return null;
  if ((paidAt.get(sender) ?? []).some((when) => when === null || when <= at)) return null;
  const look = formOf(sender);
  let best = null;
  for (const { payee, candidate: payment } of payments) {
    if (payee === sender) continue;
    const paid = finite(payment.timestamp);
    if (paid === null || paid > at || at - paid > window) continue;
    if (!lookalikeAffixes(look, formOf(payee), policy)) continue;
    const share = valueShare(candidate, payment);
    if (share === null || share > maxShare) continue;
    if (!best || at - paid < best.afterSeconds) best = { payee, share, afterSeconds: at - paid };
  }
  return best;
}
function deriveImpersonations(rows, read, subjects, policy = DEFAULT_POISONING_POLICY, catalogued) {
  const subjectSet = /* @__PURE__ */ new Set();
  for (const s of subjects) {
    const a = poisoningAddress(s);
    if (a) subjectSet.add(a);
  }
  if (subjectSet.size === 0) return /* @__PURE__ */ new Map();
  const vouched = [];
  const unprovenSenders = /* @__PURE__ */ new Set();
  const realSenders = /* @__PURE__ */ new Set();
  const payments = [];
  const paidAt = /* @__PURE__ */ new Map();
  const realIn = [];
  for (const row of rows) {
    const candidate = read(row);
    const from = poisoningAddress(candidate.fromAddress);
    const to = poisoningAddress(candidate.toAddress);
    if (!from || !to || from === to) continue;
    if (subjectSet.has(from) && !subjectSet.has(to)) {
      const times = paidAt.get(to) ?? [];
      times.push(finite(candidate.timestamp));
      paidAt.set(to, times);
      if (!catalogued?.has(to)) {
        vouched.push({ address: to, paid: true });
        if (provenPayment(candidate)) payments.push({ payee: to, candidate });
      }
      continue;
    }
    if (subjectSet.has(to) && !subjectSet.has(from)) {
      if (catalogued?.has(from)) {
        unprovenSenders.add(from);
      } else if (movedRealValue(candidate)) {
        vouched.push({ address: from, paid: false });
        realIn.push({ sender: from, candidate });
      } else {
        unprovenSenders.add(from);
      }
    }
  }
  const forms = /* @__PURE__ */ new Map();
  const formOf = (address) => {
    let form = forms.get(address);
    if (form === void 0) {
      form = displayForm(address);
      forms.set(address, form);
    }
    return form;
  };
  const mirrorsOf = /* @__PURE__ */ new Map();
  for (const { sender, candidate } of realIn) {
    const mirror = mirrorOf(sender, candidate, payments, paidAt, formOf, policy);
    if (!mirror) {
      realSenders.add(sender);
      continue;
    }
    unprovenSenders.add(sender);
    const own = mirrorsOf.get(sender) ?? /* @__PURE__ */ new Map();
    own.set(mirrorKey(candidate), mirror);
    mirrorsOf.set(sender, own);
  }
  const genuine = /* @__PURE__ */ new Set();
  for (const { address, paid } of vouched) {
    if (paid || realSenders.has(address)) genuine.add(address);
  }
  const out = /* @__PURE__ */ new Map();
  for (const sender of unprovenSenders) {
    if (realSenders.has(sender) || subjectSet.has(sender)) continue;
    const look = formOf(sender);
    let best = null;
    for (const target of genuine) {
      if (target === sender) continue;
      const shared = lookalikeAffixes(look, formOf(target), policy);
      if (!shared) continue;
      if (!best || shared.prefix + shared.suffix > best.prefix + best.suffix) {
        best = { sender, mimics: target, ...shared };
      }
    }
    if (!best) continue;
    const mirrors = mirrorsOf.get(sender);
    out.set(sender, mirrors ? { ...best, mirrors } : best);
  }
  return out;
}
function truncated(address, head, tail) {
  const form = displayForm(address);
  if (!form) return address;
  const { lead, body } = form;
  if (head + tail >= body.length) return lead + body;
  return `${lead}${body.slice(0, head)}\u2026${body.slice(body.length - tail)}`;
}
function short(address, prefix, suffix) {
  return truncated(address, Math.max(4, prefix), Math.max(4, suffix));
}
function revealing(address, prefix, suffix) {
  return truncated(address, Math.max(4, prefix + 2), Math.max(4, suffix + 2));
}
function shareText(share) {
  const percent = share * 100;
  const shown = Number(percent.toFixed(percent >= 1 ? 1 : 2));
  return shown < 0.1 ? "under 0.1%" : `${shown}%`;
}
function delayText(seconds) {
  if (seconds <= 0) return "in the same second";
  const after = (n, word) => `${n} ${word}${n === 1 ? "" : "s"} after it`;
  if (seconds < 120) return after(Math.round(seconds), "second");
  if (seconds < 2 * 3600) return after(Math.round(seconds / 60), "minute");
  if (seconds < 2 * 86400) return after(Math.round(seconds / 3600), "hour");
  return after(Math.round(seconds / 86400), "day");
}
function namesNothingBeyondATransfer(row) {
  const claimed = claimedProtocols(row);
  return claimed.length > 0 && claimed.every((id) => id === FALLBACK_PROTOCOL_ID || BARE_TRANSFER_PROTOCOLS.has(id));
}
function poisoningReason(row, impersonations, catalogued) {
  const sender = poisoningAddress(row.fromAddress);
  if (!sender) return null;
  const hit = impersonations.get(sender);
  const tagged = catalogued?.has(sender) === true;
  if (!hit && !tagged) return null;
  const real = movedRealValue(row);
  const mirror = real ? hit?.mirrors?.get(mirrorKey(row)) : void 0;
  if (real && !mirror) return null;
  if (!namesNothingBeyondATransfer(row)) return null;
  if (!hit) {
    return `Sent from ${sender}, which the catalog tags as an address-poisoning lookalike (${CATALOGUED_BY}). Address poisoning: the transfer plants the lookalike in this history so a later copy-paste pays it \u2014 compare any address copied from here character by character against the one you mean to pay.`;
  }
  const look = revealing(hit.sender, hit.prefix, hit.suffix);
  const genuine = revealing(hit.mimics, hit.prefix, hit.suffix);
  const disguise = short(hit.sender, hit.prefix, hit.suffix);
  const alsoTagged = tagged ? ` The catalog tags the sender as an address-poisoning lookalike too.` : "";
  if (mirror) {
    const shared = mirror.payee === hit.mimics ? hit : sharedAffixes(hit.sender, mirror.payee) ?? hit;
    const payee = mirror.payee === hit.mimics ? genuine : revealing(mirror.payee, shared.prefix, shared.suffix);
    return `Sent from ${look}, a lookalike of ${genuine} \u2014 an address this history genuinely transacted with. Both display as ${disguise} (${hit.prefix} leading and ${hit.suffix} trailing characters match), which is what the attack relies on. It moved real value, but it mirrors this history's own payment to ${payee} \u2014 ${shareText(mirror.share)} of the amount, ${delayText(mirror.afterSeconds)} \u2014 so the lookalike sits beside the real payee in this history. Address poisoning: the transfer plants the lookalike so a later copy-paste pays it.` + alsoTagged;
  }
  return `Sent from ${look}, a lookalike of ${genuine} \u2014 an address this history genuinely transacted with. Both display as ${disguise} (${hit.prefix} leading and ${hit.suffix} trailing characters match), which is what the attack relies on. Address poisoning: the transfer plants the lookalike so a later copy-paste pays it.` + alsoTagged;
}
var CATALOGUED_BY = "its own activity across other wallets, or an explorer's report";
function paidLookalikeReason(row, impersonations, subjects, catalogued) {
  const from = poisoningAddress(row.fromAddress);
  const to = poisoningAddress(row.toAddress);
  if (!from || !to || !subjects.has(from)) return null;
  const hit = impersonations.get(to);
  if (!hit && catalogued?.has(to) !== true) return null;
  if (!movedRealValue(row)) return null;
  if (!hit) {
    return `Paid ${to}, which the catalog tags as an address-poisoning lookalike (${CATALOGUED_BY}). This is what address poisoning plants the lookalike for: the funds went to the impersonator, not the payee it imitates.`;
  }
  const look = revealing(hit.sender, hit.prefix, hit.suffix);
  const real = revealing(hit.mimics, hit.prefix, hit.suffix);
  const disguise = short(hit.sender, hit.prefix, hit.suffix);
  return `Paid ${look}, a lookalike of ${real} \u2014 the address this history genuinely paid. Both display as ${disguise}. This is what address poisoning plants the lookalike for: the funds went to the impersonator, not the intended payee.`;
}
function unvouchedTokens(tree, pricing) {
  const counterfeit = /* @__PURE__ */ new Set();
  const known = /* @__PURE__ */ new Set();
  const seen = /* @__PURE__ */ new Set();
  const visit = (node, depth) => {
    if (depth > 64) return;
    for (const asset of node.assets ?? []) {
      const token = normalise(asset.token_address);
      if (!token) continue;
      seen.add(token);
      if (asset.impersonates) counterfeit.add(token);
      const named = typeof asset.symbol === "string" && asset.symbol.trim() !== "" || asset.decimals != null;
      const priced = asset.usd != null && String(asset.usd).trim() !== "";
      if (named || priced) known.add(token);
    }
    for (const child of node.children ?? []) visit(child, depth + 1);
  };
  for (const root of tree?.roots ?? []) visit(root, 0);
  const out = new Set(counterfeit);
  if (pricing?.priced === true) {
    for (const token of seen) if (!known.has(token)) out.add(token);
  }
  return out;
}
var SPOOF_BATCH_MIN_SENDERS = 3;
var ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
var TOKEN_KINDS = /* @__PURE__ */ new Set(["erc20", "nft"]);
function amount(raw) {
  const text = (raw ?? "").trim();
  if (!/^(0x[0-9a-f]+|\d+)$/i.test(text)) return null;
  try {
    return BigInt(text);
  } catch {
    return null;
  }
}
function isZero(raw) {
  return amount(raw) === 0n;
}
function movesSomething(raw) {
  const value = amount(raw);
  return value !== null && value > 0n;
}
function fabricatesSender(leg, called, evidence) {
  if (leg.kind === "erc20" && isZero(leg.value)) {
    return corroboratedZeroTransfer(leg, called, evidence);
  }
  return writesTheCalledContractsLedger(leg, called) || dispatchesUnvouchedToken(leg, called, evidence?.unvouchedTokens);
}
function dispatchesUnvouchedToken(leg, called, unvouched) {
  if (!unvouched || unvouched.size === 0 || leg.kind !== "erc20" || !movesSomething(leg.value)) return false;
  const target = normalise(called);
  const from = normalise(leg.from);
  const token = normalise(leg.token);
  if (target === null || !from || !token || from === ZERO_ADDRESS) return false;
  if (from === target || token === target) return false;
  return unvouched.has(token);
}
function writesTheCalledContractsLedger(leg, called) {
  const target = normalise(called);
  const from = normalise(leg.from);
  if (target === null || !from || from === target) return false;
  return normalise(leg.token) === target;
}
function movesNothing(leg, called) {
  const from = normalise(leg.from);
  if (leg.kind !== "erc20" || !from || from === normalise(called)) return false;
  return isZero(leg.value);
}
function corroboratedZeroTransfer(leg, called, evidence) {
  if (!movesNothing(leg, called)) return false;
  return [normalise(leg.from), normalise(leg.to)].some(
    (address) => address !== null && (evidence?.impersonations?.has(address) === true || evidence?.catalogued?.has(address) === true)
  );
}
function spoofedTransfers(input) {
  const signer = normalise(input.signer);
  const called = normalise(input.called);
  const claimed = [];
  for (const t of input.transfers) {
    if (!TOKEN_KINDS.has(String(t.kind ?? ""))) continue;
    const from = normalise(t.from);
    const to = normalise(t.to);
    if (!from || from === ZERO_ADDRESS || from === signer) continue;
    claimed.push({ ...t, from, to, token: normalise(t.token) });
  }
  const count = (legs) => ({
    senders: new Set(legs.map((l) => l.from)).size,
    recipients: new Set(legs.map((l) => l.to).filter(Boolean)).size,
    tokens: new Set(legs.map((l) => l.token).filter(Boolean)).size
  });
  const emittedByCalled = claimed.filter(
    (l) => writesTheCalledContractsLedger(l, called) && !(l.kind === "erc20" && isZero(l.value))
  );
  const batch = count(emittedByCalled);
  if (batch.senders >= SPOOF_BATCH_MIN_SENDERS) {
    return {
      kind: "batch",
      legs: emittedByCalled,
      ...batch,
      reason: `The contract this transaction called logged ${emittedByCalled.length} transfers of its own token claiming to come from ${batch.senders} different wallets, none of which signed it. A token only moves a holder's balance on that holder's signature or approval, so these entries are fabricated \u2014 the way an address-poisoning campaign plants lookalike senders in ${batch.recipients} wallet${batch.recipients === 1 ? "'s" : "s'"} histories at once. Do not copy an address from these transfers.`
    };
  }
  const dispatched = everyTokenUnvouched(input.transfers, input.unvouchedTokens) ? claimed.filter((l) => dispatchesUnvouchedToken(l, called, input.unvouchedTokens)) : [];
  const dispatch = count(dispatched);
  if (dispatch.senders >= SPOOF_BATCH_MIN_SENDERS) {
    const n = (value) => value.toLocaleString("en-US");
    return {
      kind: "dispatcher",
      legs: dispatched,
      ...dispatch,
      reason: `This transaction called a dispatcher that made ${n(dispatch.tokens)} counterfeit token${dispatch.tokens === 1 ? "" : "s"} log ${n(dispatched.length)} transfers claiming to come from ${n(dispatch.senders)} wallets, none of which signed it. Nothing vouches for ${dispatch.tokens === 1 ? "that token" : "those tokens"} \u2014 the decode could neither name nor price ${dispatch.tokens === 1 ? "it" : "them"}, or ${dispatch.tokens === 1 ? "it claims" : "they claim"} to be an asset ${dispatch.tokens === 1 ? "it is" : "they are"} not. A token only moves a holder's balance on that holder's signature or approval, so these entries are fabricated \u2014 the way an address-poisoning campaign plants lookalike senders in ${n(dispatch.recipients)} wallet${dispatch.recipients === 1 ? "'s" : "s'"} histories at once. Do not copy an address from these transfers.`
    };
  }
  const zero = claimed.filter((l) => corroboratedZeroTransfer(l, called, input));
  if (zero.length > 0) {
    const z = count(zero);
    return {
      kind: "zero_value",
      legs: zero,
      ...z,
      reason: `${zero.length} zero-value transfer${zero.length === 1 ? "" : "s"} name${zero.length === 1 ? "s" : ""} a sender that did not sign this transaction and ${zero.length === 1 ? "involves" : "involve"} a lookalike independently identified by the supplied history or catalog evidence. A zero amount alone does not establish poisoning. Check the full address before copying it from ${zero.length === 1 ? "this transfer" : "these transfers"}.`
    };
  }
  return null;
}
function everyTokenUnvouched(transfers, unvouched) {
  if (!unvouched || unvouched.size === 0) return false;
  let any = false;
  for (const t of transfers) {
    if (!TOKEN_KINDS.has(String(t.kind ?? ""))) continue;
    const token = normalise(t.token);
    if (!token || !unvouched.has(token)) return false;
    any = true;
  }
  return any;
}

export { DEFAULT_POISONING_POLICY, NATIVE_ASSET, POISONING_CATALOG_TAGS, POISONING_CHECK_ID, POISONING_POLICY_PARAMS, SPOOF_BATCH_MIN_SENDERS, clampPoisoningPolicy, deriveImpersonations, displayForm, evmAddress, fabricatesSender, isLookalike, isPoisoningCatalogTag, mirrorKey, paidLookalikeReason, poisoningAddress, poisoningPolicyFromParams, poisoningPolicyWithinLimits, poisoningReason, sharedAffixes, spoofedTransfers, unvouchedTokens };
//# sourceMappingURL=chunk-5KYEBYP7.js.map
//# sourceMappingURL=chunk-5KYEBYP7.js.map